<template>
    <div
        class="dashboard-templates-vertical-view"
        style="direction: ltr"
    >
        <div class="dashboard-templates-vertical-view__sidebar">
            <div class="dashboard-templates-vertical-view__sidebar-header">
                <span class="text-sm font-semibold main-text-color">
                    {{ $t('dashboard.categories') }}
                </span>
            </div>
            <div class="dashboard-templates-vertical-view__categories">
                <DashboardTemplateCategoryItem
                    v-for="category in filteredCategories"
                    :key="category.DashboardTemplateCategoryID"
                    :category="category"
                    :is-active="isActiveCategory(category)"
                    :language-filter-value="templatesLanguageFilterValue"
                    @select="onSelectCategory"
                />
            </div>
        </div>
        <div class="dashboard-templates-vertical-view__content">
            <div
                v-if="activeCategory"
                class="dashboard-templates-vertical-view__content-inner"
            >
                <div class="dashboard-templates-vertical-view__content-header">
                    <div class="flex flex-col gap-1">
                        <h3 class="text-lg font-semibold main-text-color">
                            {{ activeCategory.DashboardTemplateCategoryName }}
                        </h3>
                        <p
                            v-if="activeCategory.DashboardTemplateCategoryDescription"
                            class="text-sm text-secondary-actions"
                        >
                            {{ activeCategory.DashboardTemplateCategoryDescription }}
                        </p>
                    </div>
                </div>
                <div class="dashboard-templates-vertical-view__templates">
                    <WidgetTemplateCard
                        v-for="template in filteredTemplates"
                        :key="`template-${template.DashboardTemplateID}`"
                        :dashboard-template="template"
                        :selected-template="selectedTemplate"
                        class="dashboard-templates-vertical-view__template-card"
                        @on-detailed-view="onDetailedView(template)"
                        @on-select-template="onSelectTemplate(template)"
                    />
                    <div
                        v-if="!filteredTemplates.length"
                        class="dashboard-templates-vertical-view__empty"
                    >
                        <span class="text-secondary-actions text-sm">
                            {{ $t('dashboard.noTemplatesInCategory') }}
                        </span>
                    </div>
                </div>
            </div>
            <div
                v-else
                class="dashboard-templates-vertical-view__placeholder"
            >
                <span class="text-secondary-actions text-sm">
                    {{ $t('dashboard.selectCategoryToViewTemplates') }}
                </span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { DashboardTemplateCategoryList, DashboardTemplateList } from '@/types/dashboard-template'
import DashboardTemplateCategoryItem from '@/views/DashboardCreation/components/DashboardTemplateCategoryItem.vue'
import WidgetTemplateCard from '@/views/DashboardCreation/components/TemplateCard.vue'
import { LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE } from '@/enum/default-layout-config'

const props = withDefaults(
    defineProps<{
        templateCategories?: Array<DashboardTemplateCategoryList>
        selectedTemplate?: DashboardTemplateList
        templatesLanguageFilterValue?: number
    }>(),
    {
        templateCategories: () => [],
        selectedTemplate: undefined,
        templatesLanguageFilterValue: LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE
    }
)

const emit = defineEmits<{
    (e: 'on-detailed-view', payload: DashboardTemplateList): void
    (e: 'on-select-template', payload: DashboardTemplateList): void
}>()

const activeCategoryId = ref<number | null>(null)

const sortedCategories = computed(() => {
    return [ ...props.templateCategories ].sort((a, b) => {
        const orderA = a.DashboardTemplateCategoryConfig?.order
        const orderB = b.DashboardTemplateCategoryConfig?.order

        // Both have order - sort by order
        if (orderA !== undefined && orderB !== undefined) {
            return orderA - orderB
        }
        // Only a has order - a comes first
        if (orderA !== undefined) {
            return -1
        }
        // Only b has order - b comes first
        if (orderB !== undefined) {
            return 1
        }
        // Neither has order - sort by ID
        return a.DashboardTemplateCategoryID - b.DashboardTemplateCategoryID
    })
})

function getCategoryFilteredTemplatesCount (category: DashboardTemplateCategoryList) {
    const templates = category.DashboardTemplateList || []
    
    if (props.templatesLanguageFilterValue === LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE) {
        return templates.length
    }
    
    return templates.filter(template => template.LanguageID === props.templatesLanguageFilterValue).length
}

const filteredCategories = computed(() => {
    return sortedCategories.value.filter(category => getCategoryFilteredTemplatesCount(category) > 0)
})

const activeCategory = computed(() => {
    if (!activeCategoryId.value) return null
    return filteredCategories.value.find(
        cat => cat.DashboardTemplateCategoryID === activeCategoryId.value
    ) || null
})

const filteredTemplates = computed(() => {
    if (!activeCategory.value) return []

    const templates = activeCategory.value.DashboardTemplateList || []

    if (props.templatesLanguageFilterValue === LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE) {
        return sortTemplates(templates)
    }

    return sortTemplates(
        templates.filter(template => template.LanguageID === props.templatesLanguageFilterValue)
    )
})

function sortTemplates (templates: DashboardTemplateList[]) {
    return [ ...templates ].sort((a, b) => a.DashboardTemplateOrder - b.DashboardTemplateOrder)
}

function isActiveCategory (category: DashboardTemplateCategoryList) {
    return activeCategoryId.value === category.DashboardTemplateCategoryID
}

function onSelectCategory (category: DashboardTemplateCategoryList) {
    activeCategoryId.value = category.DashboardTemplateCategoryID
}

function onSelectTemplate (template: DashboardTemplateList) {
    emit('on-select-template', template)
}

function onDetailedView (template: DashboardTemplateList) {
    emit('on-detailed-view', template)
}

function selectCategoryById (categoryId: string | number) {
    activeCategoryId.value = Number(categoryId)
}

function getActiveCategoryId () {
    return activeCategoryId.value?.toString() || ''
}

// Auto-select first category when categories are loaded or when filter changes
watch(
    filteredCategories,
    (categories) => {
        if (!categories?.length) {
            activeCategoryId.value = null
            return
        }
        
        // If no category is selected or current category is filtered out, select first one
        const isCurrentCategoryAvailable = categories.some(
            cat => cat.DashboardTemplateCategoryID === activeCategoryId.value
        )
        
        if (!activeCategoryId.value || !isCurrentCategoryAvailable) {
            activeCategoryId.value = categories[0].DashboardTemplateCategoryID
        }
    },
    { immediate: true }
)

defineExpose({
    selectTab: selectCategoryById,
    getActiveTabName: getActiveCategoryId
})
</script>

<style lang="scss" scoped>
.dashboard-templates-vertical-view {
    display: flex;
    width: 100%;
    border-radius: 0.5rem;
    border: 1px solid var(--ui-lines);
    overflow: hidden;
    background: var(--main-bg);
    height: 520px;

    &__sidebar {
        flex-shrink: 0;
        border-right: 1px solid var(--ui-lines);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        background: var(--secondaryBgColor-color);
        width: 220px;
    }

    &__sidebar-header {
        padding: 0.75rem;
        border-bottom: 1px solid var(--ui-lines);
    }

    &__categories {
        flex-grow: 1;
        overflow-y: auto;
        padding: 0.5rem;

        > * + * {
            margin-top: 0.25rem;
        }
    }

    &__content {
        flex-grow: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        background: var(--light-bg);
    }

    &__content-inner {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__content-header {
        padding: 0.75rem 1rem;
        border-bottom: 1px solid var(--ui-lines);
        flex-shrink: 0;
        background: var(--secondaryBgColor-color);
    }

    &__templates {
        flex-grow: 1;
        overflow-y: auto;
        padding: 1rem;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        gap: 12px;
        align-content: start;
    }

    &__template-card {
        height: fit-content;
    }

    &__empty,
    &__placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }
}

/* Responsive adjustments for larger screens */
@media (min-width: 1280px) {
    .dashboard-templates-vertical-view {
        height: 600px;

        &__sidebar {
            width: 240px;
        }

        &__templates {
            grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
            gap: 16px;
        }
    }
}
</style>

